import React, { lazy, Suspense, useEffect } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { LoginCallback } from '@okta/okta-react';
import * as Sentry from '@sentry/react';

import oktaAuth from '@/config/oktaConfig';
import AuthGuard from '@/routes/AuthGuard';

import ApplicationSettingsPage from '@/pages/ApplicationSettingsPage';
import LoadingPage from '@/pages/common/LoadingPage';
import EpicLoginHandoffPage from '@/pages/EpicLoginHandoffPage';
import LoadReport from '@/pages/physician/LoadReport';
import PatientListPage from '@/pages/physician/PatientListPage';
import PatientOverviewPage from '@/pages/physician/PatientOverviewPage';
import { TransmissionReportRedirectPage } from '@/pages/physician/TransmissionReportRedirectPage';

import routes from './config';

// Common Pages
const LoadingScreen = () => (
  <LoadingPage title="Loading" message="Please wait while we load your application..." />
);

const LoginRedirect = () => (
  <LoadingPage title="Redirecting" message="Logging you in, almost there..." />
);

// Physician Pages
const TransmissionReportPage = lazy(() => import('@/pages/physician/TransmissionReportPage'));
const InClinicPage = lazy(() => import('@/pages/physician/InClinicPage'));
const Dashboard = lazy(() => import('@/pages/physician/DashboardPage'));
const SchedulePage = lazy(() => import('@/pages/physician/SchedulePage'));
const TasksPage = lazy(() => import('@/pages/physician/TasksPage'));
const TaskDetailPage = lazy(() => import('@/pages/physician/TasksPage/TaskDetailPage'));

// Analytics Pages
const AnalyticsLayout = lazy(() => import('@/pages/physician/analytics/components/Layout'));
const AccountOverview = lazy(
  () => import('@/pages/physician/analytics/components/AccountOverview')
);
const AlertsPage = lazy(() => import('@/pages/physician/analytics/components/Alerts'));
const BillingPage = lazy(() => import('@/pages/physician/analytics/components/Billing'));
const SignatureStatusPage = lazy(
  () => import('@/pages/physician/analytics/components/SignatureStatus')
);
const LeadsRecallsPage = lazy(() => import('@/pages/physician/analytics/components/LeadsRecall'));
const DeviceRecallsPage = lazy(() => import('@/pages/physician/analytics/components/DeviceRecall'));
const BatteryLifePage = lazy(() => import('@/pages/physician/analytics/components/BatteryLife'));

// Login error handler component with Sentry reporting
const LoginError = ({ error }: { error: Error }) => {
  const isPkce = error?.message?.includes('codeVerifier') || error?.message?.includes('PKCE');

  useEffect(() => {
    const recoverFromLoginError = async () => {
      // Use attempt counter instead of time window for better reliability
      const LOGIN_RETRY_ATTEMPTS = 'login-attempt-count';
      const MAX_RECOVERY_ATTEMPTS = 2;

      const attempts = Number(sessionStorage.getItem(LOGIN_RETRY_ATTEMPTS) ?? 0);

      if (attempts >= MAX_RECOVERY_ATTEMPTS) {
        // Show a real error UI after too many attempts
        Sentry.addBreadcrumb({
          category: 'authentication',
          level: 'error',
          message: 'Too many login attempts, showing error page',
          data: {
            attempts,
            isPkce
          }
        });

        // Navigate to a proper error page or show error UI in a component
        // For now, we'll show error page and clear all session storage
        sessionStorage.removeItem(LOGIN_RETRY_ATTEMPTS);
        sessionStorage.removeItem('login-recovery-pending');
        return;
      }

      // Get the route the user originally wanted to access.
      const originalUri = oktaAuth.getOriginalUri();

      // Fallback to dashboard if no original route exists.
      const redirectUri = originalUri || routes.physician.dashboard;

      Sentry.captureException(error, {
        tags: {
          feature: 'authentication',
          page: 'login-callback',
          pkceMissing: isPkce
        },
        extra: {
          pathname: window.location.pathname,
          originalUri,
          redirectUri,
          hasCode: window.location.search.includes('code='),
          hasState: window.location.search.includes('state=')
        }
      });

      Sentry.addBreadcrumb({
        category: 'authentication',
        level: 'warning',
        message: 'Attempting login recovery',
        data: {
          isPkce,
          originalUri,
          redirectUri,
          attempts
        }
      });

      // Increment attempt counter
      sessionStorage.setItem(LOGIN_RETRY_ATTEMPTS, String(attempts + 1));
      sessionStorage.setItem('login-recovery-pending', 'true');

      try {
        // Clear stale PKCE transaction.
        oktaAuth.transactionManager.clear();

        Sentry.addBreadcrumb({
          category: 'authentication',
          level: 'info',
          message: 'Okta transaction cleared',
          data: {
            redirectUri
          }
        });

        await Sentry.flush(2000);

        window.location.href = redirectUri;
      } catch (recoveryError) {
        Sentry.captureException(recoveryError, {
          tags: {
            feature: 'authentication',
            page: 'login-recovery'
          },
          extra: {
            originalUri,
            redirectUri
          }
        });
      }
    };

    recoverFromLoginError();
  }, [error, isPkce, oktaAuth]);

  return <LoadingPage title="Login Failed" message="Authentication failed. Please try again." />;
};

// Authenticated Routes Component
const AuthenticatedRoutes = () => {
  return (
    <Routes>
      {/* Analytics Routes */}
      <Route path={routes.physician.analytics.root} element={<AnalyticsLayout />}>
        <Route
          index
          element={<Navigate to={routes.physician.analytics.accountOverview} replace />}
        />
        <Route path="account-overview" element={<AccountOverview />} />
        <Route path="alerts" element={<AlertsPage />} />
        <Route path="billing" element={<BillingPage />} />
        <Route path="signature-status" element={<SignatureStatusPage />} />
        <Route path="battery-life" element={<BatteryLifePage />} />
        <Route path="device-recalls" element={<DeviceRecallsPage />} />
        <Route path="leads-recalls" element={<LeadsRecallsPage />} />
      </Route>

      <Route path={routes.physician.schedule} element={<SchedulePage />} />
      <Route path={routes.physician.tasks} element={<TasksPage />} />
      <Route path={routes.physician.taskDetail(':taskId')} element={<TaskDetailPage />} />
      <Route path={routes.physician.dashboard} element={<Dashboard />} />
      <Route path={routes.physician.patientsList} element={<PatientListPage />} />
      <Route path={routes.physician.patientDetail(':uuid')} element={<PatientOverviewPage />} />
      <Route path={routes.physician.inClinic} element={<InClinicPage />} />

      {/* Transmission Routes */}
      <Route
        path={routes.physician.defaultTransmissionReport}
        element={<TransmissionReportRedirectPage />}
      />
      <Route
        path={routes.physician.transmissionReportDetail(':id')}
        element={<TransmissionReportPage />}
      />
      <Route path={routes.physician.loadReport} element={<LoadReport />} />

      <Route path={routes.user.settings} element={<ApplicationSettingsPage />} />

      {/* Fallback Route */}
      <Route path="*" element={<Navigate to={routes.physician.dashboard} replace />} />
    </Routes>
  );
};

const AppRoutes: React.FC = () => {
  return (
    <Suspense fallback={<LoadingScreen />}>
      <Routes>
        {/* Public Routes - NOT wrapped in AuthGuard */}
        <Route
          path={routes.login.callback}
          element={<LoginCallback loadingElement={<LoginRedirect />} errorComponent={LoginError} />}
        />

        {/* Epic EHR launch hand-off - NOT wrapped in AuthGuard */}
        <Route path={routes.login.epic} element={<EpicLoginHandoffPage />} />

        {/* Explicit root redirect - NOT wrapped in AuthGuard */}
        <Route path={routes.root} element={<Navigate to="/physician/dashboard" replace />} />

        {/* Protected Routes - wrapped in AuthGuard */}
        <Route element={<AuthGuard />}>
          <Route path="/*" element={<AuthenticatedRoutes />} />
        </Route>
      </Routes>
    </Suspense>
  );
};

export default AppRoutes;
